import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import PaymentService from '../../services/payment.service';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';

const PaymentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchPayment();
    }
  }, [id]);

  const fetchPayment = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await PaymentService.getOne(Number(id));
      setPayment(response.data?.data);
    } catch (error: any) {
      console.error('Error fetching payment:', error);
      setError(error.response?.data?.error?.message || 'Failed to load payment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await PaymentService.delete(Number(id));
        navigate('/payments');
      } catch (error) {
        console.error('Error deleting payment:', error);
        alert('Failed to delete payment');
      }
    }
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'success';
      case 'card':
        return 'primary';
      case 'bankTransfer':
        return 'info';
      case 'check':
        return 'secondary';
      case 'other':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Cash';
      case 'card':
        return 'Card';
      case 'bankTransfer':
        return 'Bank Transfer';
      case 'check':
        return 'Check';
      case 'other':
        return 'Other';
      default:
        return method;
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!payment) return;

    const doc = new jsPDF();
    const data = payment.attributes;
    const invoice = data.invoice?.data?.attributes;
    const customer = invoice?.customer?.data?.attributes;
    const receivedBy = data.receivedBy?.data?.attributes;

    // Load and add logo
    await addLogoToPDF(doc);

    // Add company name
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

    // Add header
    doc.setFontSize(24);
    doc.setTextColor(33, 150, 243);
    doc.text('PAYMENT RECEIPT', 105, 20, { align: 'center' });

    // Add payment number
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Receipt #: ${data.paymentNumber}`, 105, 30, { align: 'center' });

    // Payment date
    doc.setFontSize(10);
    doc.text(`Date: ${formatDate(data.paymentDate)}`, 105, 37, { align: 'center' });

    // Customer information
    let yPos = 50;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 7;
    doc.setFontSize(10);
    doc.text(customer?.companyName || customer?.contactPerson || '-', 20, yPos);
    
    if (customer?.email) {
      yPos += 5;
      doc.text(`Email: ${customer.email}`, 20, yPos);
    }
    if (customer?.phone) {
      yPos += 5;
      doc.text(`Phone: ${customer.phone}`, 20, yPos);
    }

    // Related document information
    yPos += 15;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Details:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 7;
    doc.setFontSize(10);
    doc.text(`Number: ${invoice?.invoiceNumber || '-'}`, 20, yPos);
    yPos += 5;
    doc.text(`Date: ${formatDate(invoice?.invoiceDate)}`, 20, yPos);
    yPos += 5;
    doc.text(`Due Date: ${formatDate(invoice?.dueDate)}`, 20, yPos);

    // Payment details box
    yPos += 15;
    doc.setDrawColor(33, 150, 243);
    doc.setLineWidth(0.5);
    doc.rect(15, yPos, 180, 50);

    yPos += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Details', 20, yPos);
    
    yPos += 10;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Amount Paid:', 20, yPos);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 128, 0);
    doc.text(`OMR ${(data.amount || 0).toFixed(2)}`, 190, yPos, { align: 'right' });
    
    yPos += 8;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text('Payment Method:', 20, yPos);
    doc.text(getMethodLabel(data.paymentMethod), 190, yPos, { align: 'right' });
    
    if (data.reference) {
      yPos += 8;
      doc.text('Reference:', 20, yPos);
      doc.text(data.reference, 190, yPos, { align: 'right' });
    }

    // Summary
    yPos += 20;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 7;
    doc.setFontSize(10);
    doc.text('Total:', 20, yPos);
    doc.text(`OMR ${(invoice?.total || 0).toFixed(2)}`, 190, yPos, { align: 'right' });
    yPos += 5;
    doc.text('Previous Payments:', 20, yPos);
    doc.text(`OMR ${((invoice?.paidAmount || 0) - (data.amount || 0)).toFixed(2)}`, 190, yPos, { align: 'right' });
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('This Payment:', 20, yPos);
    doc.text(`OMR ${(data.amount || 0).toFixed(2)}`, 190, yPos, { align: 'right' });
    yPos += 5;
    doc.setDrawColor(0, 0, 0);
    doc.line(20, yPos, 190, yPos);
    yPos += 5;
    doc.text('Balance Due:', 20, yPos);
    doc.text(`OMR ${((invoice?.total || 0) - (invoice?.paidAmount || 0)).toFixed(2)}`, 190, yPos, { align: 'right' });

    // Notes
    if (data.notes) {
      yPos += 15;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 20, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 5;
      const splitNotes = doc.splitTextToSize(data.notes, 170);
      doc.text(splitNotes, 20, yPos);
      yPos += splitNotes.length * 5;
    }

    // Received by
    if (receivedBy) {
      yPos += 10;
      doc.setFontSize(9);
      doc.text(`Received by: ${receivedBy.firstName} ${receivedBy.lastName}`, 20, yPos);
    }

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Thank you for your payment!', 105, pageHeight - 20, { align: 'center' });
    doc.text(`Generated on ${new Date().toLocaleString()}`, 105, pageHeight - 15, { align: 'center' });

    // Save the PDF
    doc.save(`Payment-Receipt-${data.paymentNumber}.pdf`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/payments')}
          sx={{ mb: 2 }}
        >
          Back to Payments
        </Button>
        <Alert severity="error">{error || 'Payment not found'}</Alert>
      </Box>
    );
  }

  const data = payment.attributes;
  const isExpense = data.isExpense || false;
  const invoice = data.invoice?.data?.attributes;
  const customer = invoice?.customer?.data?.attributes;
  const receivedBy = data.receivedBy?.data?.attributes;
  const account = data.account?.data?.attributes;

  return (
    <Box>
      {/* Print-specific styles */}
      <style>
        {`
          .print-only {
            display: none;
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-area, .print-area * {
              visibility: visible;
            }
            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            @page {
              margin: 1cm;
            }
          }
        `}
      </style>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/payments')}>
            Back
          </Button>
          <Typography variant="h4">
            <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {data.paymentNumber}
          </Typography>
          <Chip
            label={isExpense ? 'Expense' : 'Payment'}
            color={isExpense ? 'error' : 'success'}
            size="medium"
          />
          <Chip
            label={getMethodLabel(data.paymentMethod)}
            color={getMethodColor(data.paymentMethod)}
            size="medium"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPDF}>
            Download PDF
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/payments/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      {/* Payment Details */}
      <Stack spacing={3} className="print-area">
        {/* Print Header */}
        <Box className="print-only" sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom>
            {isExpense ? 'EXPENSE RECORD' : 'PAYMENT RECEIPT'}
          </Typography>
          <Typography variant="h5">{data.paymentNumber}</Typography>
          <Typography variant="body1" color="text.secondary">
            {formatDate(data.paymentDate)}
          </Typography>
        </Box>

        {/* Payment/Expense Amount */}
        <Card sx={{ bgcolor: isExpense ? 'error.light' : 'success.light' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" color={isExpense ? 'error.contrastText' : 'success.contrastText'}>
                  {isExpense ? 'Expense Amount' : 'Amount Paid'}
                </Typography>
                <Typography variant="caption" color={isExpense ? 'error.contrastText' : 'success.contrastText'}>
                  {formatDate(data.paymentDate)}
                </Typography>
              </Box>
              <Typography variant="h2" color={isExpense ? 'error.contrastText' : 'success.contrastText'} fontWeight="bold">
                {formatCurrency(data.amount)}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Main Information */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {isExpense ? (
              <>
                {/* Expense Details */}
                <Box sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '300px' }}>
                  <Typography variant="h6" gutterBottom>
                    Expense Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Category:
                      </Typography>
                      <Chip
                        label={data.category?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || '-'}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    {data.description && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Description:
                        </Typography>
                        <Typography variant="body1">
                          {data.description}
                        </Typography>
                      </Box>
                    )}
                    {account && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Account:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {account.accountNumber} - {account.accountName}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '300px' }} />
              </>
            ) : (
              <>
                {/* Customer Info */}
                <Box sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '300px' }}>
                  <Typography variant="h6" gutterBottom>
                    Customer
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1}>
                    <Typography variant="body1" fontWeight="bold">
                      {customer?.companyName || customer?.contactPerson || '-'}
                    </Typography>
                    {customer?.email && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.email}
                      </Typography>
                    )}
                    {customer?.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.phone}
                      </Typography>
                    )}
                    {customer?.address && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.address}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                {/* Related document info */}
                <Box sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '300px' }}>
                  <Typography variant="h6" gutterBottom>
                    Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        #:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => navigate(`/invoices/${data.invoice?.data?.id}`)}
                      >
                        {invoice?.invoiceNumber || '-'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Date:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(invoice?.invoiceDate)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Due Date:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(invoice?.dueDate)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Total:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(invoice?.total)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </Paper>

        {/* Payment Details */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payment Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
              <Typography variant="body2" color="text.secondary">
                Payment Method
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {getMethodLabel(data.paymentMethod)}
              </Typography>
            </Box>
            {data.reference && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                <Typography variant="body2" color="text.secondary">
                  Reference / Transaction ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.reference}
                </Typography>
              </Box>
            )}
            {receivedBy && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                <Typography variant="body2" color="text.secondary">
                  Received By
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {receivedBy.firstName} {receivedBy.lastName} ({receivedBy.employeeNumber})
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Balance - Only show for payments */}
        {!isExpense && invoice && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Balance
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Total:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(invoice?.total)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Total Paid:</Typography>
                  <Typography variant="body1" fontWeight="medium" color="success.main">
                    {formatCurrency(invoice?.paidAmount)}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6">Balance Due:</Typography>
                  <Typography variant="h6" fontWeight="bold" color={
                    (invoice?.total - invoice?.paidAmount) > 0 ? 'error.main' : 'success.main'
                  }>
                    {formatCurrency(invoice?.total - invoice?.paidAmount)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {data.notes && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {data.notes}
            </Typography>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default PaymentView;

